import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Bell, BellOff, Plus, Trash2, Calendar as CalendarIcon } from "lucide-react"
import { format, parseISO } from "date-fns"
import { ru } from "date-fns/locale"
import { usePersonalRemindersStore } from "@/store/personalRemindersStore"
import { cn } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface TodayRemindersProps {
  selectedDate: string  // "yyyy-MM-dd"
}

export function TodayReminders({ selectedDate }: TodayRemindersProps) {
  const { reminders, addReminder, deleteReminder } = usePersonalRemindersStore()

  const selectedDateObj = parseISO(selectedDate)
  const dateTitle = format(selectedDateObj, "d MMMM yyyy", { locale: ru })

  const dateReminders = reminders
    .filter(r => r.date === selectedDate)
    .sort((a, b) => a.title.localeCompare(b.title))

  const [newTitle, setNewTitle] = useState("")
  const [newDate, setNewDate] = useState<Date>(selectedDateObj)
  const [newTime, setNewTime] = useState("")
  const [isAdding, setIsAdding] = useState(false)

  // ← Исправление: синхронизируем дату при смене выбранного дня в календаре
  useEffect(() => {
    if (!isAdding) {
      setNewDate(selectedDateObj)
    }
  }, [selectedDate, isAdding])

  const handleAdd = async () => {
    if (!newTitle.trim() || !newDate) return

    const dateStr = format(newDate, "yyyy-MM-dd")

    await addReminder(dateStr, newTitle.trim(), newTime || undefined)

    // Сброс формы
    setNewTitle("")
    setNewDate(selectedDateObj)
    setNewTime("")
    setIsAdding(false)
  }

  return (
    <Card className={cn(
      "mt-9 lg:mt-0 lg:ml-9 w-full lg:w-96 xl:w-[420px]",
      "bg-gradient-to-br from-orange-950/70 to-amber-950/70",
      "border border-orange-600/50 rounded-3xl",
      "shadow-2xl shadow-orange-900/50 backdrop-blur-sm overflow-hidden",
    )}>
      <CardHeader className="pb-3 border-b border-orange-700/40 bg-orange-950/40">
        <CardTitle className="text-xl font-bold text-orange-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dateReminders.length > 0 ? (
              <Bell className="h-5 w-5 text-orange-400 animate-pulse" />
            ) : (
              <BellOff className="h-5 w-5 text-orange-600" />
            )}
            Напоминания на {dateTitle}
          </div>
          <span className="text-sm font-medium bg-orange-600/30 px-2 py-1 rounded-full">
            {dateReminders.length}
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-5 space-y-4">
        {dateReminders.length === 0 ? (
          <div className="text-center py-10 text-orange-400/70 italic">
            <BellOff className="h-14 w-14 mx-auto mb-4 opacity-60" />
            <p className="text-lg">Нет напоминаний на эту дату</p>
            <Button
              size="lg"
              onClick={() => setIsAdding(true)}
              className="mt-6 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-lg"
            >
              <Plus className="mr-2 h-5 w-5" /> Добавить напоминание
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {dateReminders.map((rem) => (
              <div
                key={rem.id}
                className="flex items-center justify-between bg-orange-900/40 p-4 rounded-2xl border border-orange-600/40 group hover:bg-orange-900/60 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse flex-shrink-0" />
                  <div>
                    <p className="text-orange-100 font-medium text-base">{rem.title}</p>
                    {rem.time && (
                      <p className="text-orange-300 text-sm">в {rem.time}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteReminder(rem.id)}
                  className="h-9 w-9 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-orange-800/50 hover:text-orange-300"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            ))}

            <Button
              size="lg"
              onClick={() => setIsAdding(true)}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-md"
            >
              <Plus className="mr-2 h-5 w-5" /> Добавить ещё напоминание
            </Button>
          </div>
        )}

        {/* Форма добавления */}
        {isAdding && (
          <div className="mt-6 p-5 bg-orange-900/50 rounded-2xl border border-orange-600/50 backdrop-blur-sm space-y-5">
            <div>
              <Label className="text-orange-200 text-sm font-medium mb-2 block">
                Дата напоминания
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-orange-950/60 border-orange-600 text-orange-100",
                      !newDate && "text-orange-400"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newDate ? format(newDate, "d MMMM yyyy", { locale: ru }) : "Выберите дату"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-orange-950 border-orange-700">
                  <Calendar
                    mode="single"
                    selected={newDate}
                    onSelect={setNewDate}
                    initialFocus
                    className="rounded-md border border-orange-700"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-orange-200 text-sm font-medium mb-2 block">
                Время (опционально)
              </Label>
              <Input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="bg-orange-950/60 border-orange-600 text-orange-100"
              />
            </div>

            <div>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                placeholder="Что нужно не забыть?.."
                autoFocus
                className="w-full bg-transparent border-b border-orange-500 text-orange-100 placeholder:text-orange-400 outline-none pb-2 text-lg"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsAdding(false)
                  setNewTitle("")
                  setNewDate(selectedDateObj)
                  setNewTime("")
                }}
                className="text-orange-300 hover:text-orange-100"
              >
                Отмена
              </Button>
              <Button
                onClick={handleAdd}
                disabled={!newTitle.trim() || !newDate}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}